import { RefObject, useEffect, useState } from 'react'
import {
  PlayerLayout,
  getOrientation,
  getSidebarWidth,
  getSubtitleBase,
  getTranscriptMode,
  isNarrowControls,
} from './layout'

// Everything the player lays out from is measured here, never read off the
// window: the player is iframe-embeddable at arbitrary sizes, so the wrapper
// and video column are the only honest sources of truth.
//
// Two elements are observed because they answer different questions:
//   - the wrapper (whole player) decides orientation / transcript mode /
//     sidebar width. Deriving those from the video column would oscillate —
//     an open sidebar narrows the column to portrait, portrait unmounts the
//     sidebar, the column widens back to landscape, forever.
//   - the video column decides subtitle size, narrow controls and the height
//     the compass scales against.

function deriveLayout(wrapperW: number, wrapperH: number, columnW: number, columnH: number): PlayerLayout {
  const orientation = getOrientation(wrapperW, wrapperH)
  return {
    wrapperW,
    wrapperH,
    orientation,
    transcriptMode: getTranscriptMode(orientation),
    sidebarWidth: getSidebarWidth(wrapperW),
    columnW,
    columnH,
    subtitleBase: getSubtitleBase(columnW),
    isNarrowControls: isNarrowControls(columnW),
  }
}

const EMPTY_LAYOUT = deriveLayout(0, 0, 0, 0)

function sameLayout(a: PlayerLayout, b: PlayerLayout): boolean {
  return (
    a.wrapperW === b.wrapperW &&
    a.wrapperH === b.wrapperH &&
    a.columnW === b.columnW &&
    a.columnH === b.columnH
  )
}

export function usePlayerLayout(
  wrapperRef: RefObject<HTMLElement | null>,
  containerRef: RefObject<HTMLElement | null>,
): PlayerLayout {
  const [layout, setLayout] = useState<PlayerLayout>(EMPTY_LAYOUT)

  useEffect(() => {
    const wrapper = wrapperRef.current
    const column = containerRef.current
    if (!wrapper && !column) return

    // Latest measurements, applied to state once per frame — resize storms
    // (rotation, fullscreen transitions, sidebar open) fire both observers
    // many times per frame and would otherwise thrash rendering.
    let wrapperW = wrapper ? wrapper.clientWidth : 0
    let wrapperH = wrapper ? wrapper.clientHeight : 0
    let columnW = column ? column.clientWidth : 0
    let columnH = column ? column.clientHeight : 0
    let frame: number | null = null

    const flush = () => {
      frame = null
      const next = deriveLayout(wrapperW, wrapperH, columnW, columnH)
      setLayout((prev) => (sameLayout(prev, next) ? prev : next))
    }

    const schedule = () => {
      if (frame !== null) return
      frame = requestAnimationFrame(flush)
    }

    const observe = (el: HTMLElement | null, apply: (w: number, h: number) => void) => {
      if (!el) return null
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          apply(entry.contentRect.width, entry.contentRect.height)
        }
        schedule()
      })
      ro.observe(el)
      return ro
    }

    const wrapperRo = observe(wrapper, (w, h) => { wrapperW = w; wrapperH = h })
    const columnRo = observe(column, (w, h) => { columnW = w; columnH = h })

    flush()

    return () => {
      if (frame !== null) cancelAnimationFrame(frame)
      wrapperRo?.disconnect()
      columnRo?.disconnect()
    }
  }, [wrapperRef, containerRef])

  return layout
}

export default usePlayerLayout
